// Shared utilities for the Academy module

export const ADMIN_WA = '201008182425';

export const CATEGORY_COLORS = {
  'General English':       { color:'#1a5fa8', bg:'#e8f0fb' },
  'IELTS':                 { color:'#b8620a', bg:'#fff8f0' },
  'Speaking & Fluency':    { color:'#1a7a4a', bg:'#e8f5ee' },
  'SAT English':           { color:'#5a1a8a', bg:'#f5ebff' },
  'SAT Math':              { color:'#5a1a8a', bg:'#f5ebff' },
  'German':                { color:'#c0392b', bg:'#fff0f0' },
  'Interview Preparation': { color:'#0a6a7a', bg:'#e8f8fb' },
};

// ── localStorage session helpers ─────────────────────────────
const TEACHER_SESSION_KEY = 'ca_teacher_session';
const STUDENT_SESSION_KEY = 'ca_student_session';

export function saveTeacherSession(teacher) {
  try { localStorage.setItem(TEACHER_SESSION_KEY, JSON.stringify({ teacher, ts: Date.now() })); } catch {}
}
export function loadTeacherSession() {
  try {
    const v = localStorage.getItem(TEACHER_SESSION_KEY);
    if (!v) return null;
    const s = JSON.parse(v);
    if (Date.now() - s.ts > 12 * 3600 * 1000) { localStorage.removeItem(TEACHER_SESSION_KEY); return null; }
    return s.teacher;
  } catch { return null; }
}
export function clearTeacherSession() { try { localStorage.removeItem(TEACHER_SESSION_KEY); } catch {} }

export function saveStudentSession(student) {
  try { localStorage.setItem(STUDENT_SESSION_KEY, JSON.stringify({ student, ts: Date.now() })); } catch {}
}
export function loadStudentSession() {
  try {
    const v = localStorage.getItem(STUDENT_SESSION_KEY);
    if (!v) return null;
    const s = JSON.parse(v);
    if (Date.now() - s.ts > 12 * 3600 * 1000) { localStorage.removeItem(STUDENT_SESSION_KEY); return null; }
    return s.student;
  } catch { return null; }
}
export function clearStudentSession() { try { localStorage.removeItem(STUDENT_SESSION_KEY); } catch {} }

// ── Date/time helpers ────────────────────────────────────────
export function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short', year:'numeric' });
}
export function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' });
}
export function fmtDateTime(iso) {
  if (!iso) return '';
  return `${fmtDate(iso)} · ${fmtTime(iso)}`;
}

// ── Zoom link builder ────────────────────────────────────────
export function buildZoomLink(meetingId) {
  if (!meetingId) return '';
  return `https://zoom.us/j/${meetingId.replace(/\s/g, '')}`;
}

// ── Shared inline styles ─────────────────────────────────────
export const acBase = {
  wrap:   { minHeight:'100vh', background:'#f8f9fb', padding:'24px 16px', fontFamily:"'Plus Jakarta Sans',sans-serif" },
  card:   { background:'#fff', border:'1px solid #e8edf5', borderRadius:14, padding:20, boxShadow:'0 2px 12px rgba(26,42,74,0.07)', marginBottom:16 },
  h1:     { fontFamily:"'Cormorant Garamond',serif", fontSize:24, fontWeight:700, color:'#1a2a4a', marginBottom:6 },
  h2:     { fontFamily:"'Cormorant Garamond',serif", fontSize:18, fontWeight:700, color:'#1a2a4a', marginBottom:10 },
  sub:    { fontSize:13, color:'#5a6a88', lineHeight:1.6, marginBottom:18 },
  inp:    { width:'100%', background:'#f8f9fb', border:'1px solid #e8edf5', borderRadius:10, padding:'11px 14px', color:'#1a2233', fontSize:13.5, fontFamily:"'Plus Jakarta Sans',sans-serif", outline:'none', marginBottom:14, boxSizing:'border-box' },
  btn:    { width:'100%', background:'linear-gradient(135deg,#1a2a4a,#2a3a5a)', color:'#fff', border:'none', padding:13, borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 8px rgba(26,42,74,0.25)', letterSpacing:'0.01em' },
  btnGold:{ width:'100%', background:'linear-gradient(135deg,#b8860b,#d4a017)', color:'#fff', border:'none', padding:13, borderRadius:10, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", boxShadow:'0 2px 8px rgba(184,134,11,0.3)' },
  btnWA:  { background:'#25d366', color:'#fff', border:'none', padding:'11px 18px', borderRadius:10, fontSize:13.5, fontWeight:700, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif", display:'flex', alignItems:'center', gap:8 },
  btnOut: { background:'transparent', border:'1.5px solid #e8edf5', color:'#5a6a88', padding:'10px 18px', borderRadius:10, fontSize:13, fontWeight:500, cursor:'pointer', fontFamily:"'Plus Jakarta Sans',sans-serif" },
  back:   { background:'none', border:'none', color:'#9aa3b8', fontSize:11, cursor:'pointer', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.08em', padding:0 },
  lbl:    { fontSize:10.5, fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.14em', textTransform:'uppercase', color:'#9aa3b8', marginBottom:5, display:'block' },
  err:    { color:'#c0392b', fontSize:12.5, padding:'9px 13px', background:'#fff5f5', borderRadius:9, lineHeight:1.55, border:'1px solid rgba(192,57,43,0.2)', marginBottom:12 },
  badge: (color, bg) => ({ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, fontWeight:600, color, background:bg, border:`1px solid ${color}33`, borderRadius:100, padding:'2px 9px', fontFamily:"'Plus Jakarta Sans',sans-serif" }),
  statusBadge: (s) => ({
    fontSize:11, fontWeight:600, padding:'2px 9px', borderRadius:100, fontFamily:"'JetBrains Mono',monospace",
    color:   s==='confirmed'?'#1a7a4a':s==='completed'?'#1a5fa8':s==='cancelled'?'#c0392b':'#9aa3b8',
    background: s==='confirmed'?'#e8f5ee':s==='completed'?'#e8f0fb':s==='cancelled'?'#fff5f5':'#f7f9fc',
    border:  `1px solid ${s==='confirmed'?'#c8ead8':s==='completed'?'#c0d8f8':s==='cancelled'?'rgba(192,57,43,0.2)':'#dde3ed'}`,
  }),
};

export function WAIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

export function Spinner() {
  return (
    <>
      <style>{`@keyframes acSpin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ display:'inline-block', width:18, height:18, border:'2px solid #dde3ed', borderTopColor:'#b8860b', borderRadius:'50%', animation:'acSpin .8s linear infinite' }}/>
    </>
  );
}
